import { message } from "antd"
import { AxiosRequestConfig } from "axios"
import { t } from "i18next"
import { useState } from "react"
import { HttpClient } from "~/api/HttpClient"
import { dataPayloadBase, FilterSearchParams } from "~/types/types"

const API_PREFIX_BASESYSTEM_PATH = "/api/base-systems"

const baseService = () => {
  const [isLoading, setIsLoading] = useState(false)

  const addBase = async (data: { data: dataPayloadBase }) => {
    setIsLoading(true)
    try {
      const response = await HttpClient.post(`${API_PREFIX_BASESYSTEM_PATH}`, data)
      return response
    } catch (error) {
      message.error(t("Unable to add base. Please try again"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const fetchBaseById = async (id: number) => {
    setIsLoading(true)

    if (!id) {
      message.error(t(t("Invalid ID")))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.get(`${API_PREFIX_BASESYSTEM_PATH}/${id}?populate=*`)
      return response?.data || null
    } catch (error) {
      message.error(t("Failed to fetch base data. Please try again"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updateBase = async (id: number, data: { data: dataPayloadBase }) => {
    setIsLoading(true)

    if (!id) {
      message.error(t(t("Invalid ID")))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.put(`${API_PREFIX_BASESYSTEM_PATH}/${id}`, data)
      message.success("Trang được cập nhật thành công!")
      return response
    } catch (error) {
      message.error(t("Unable to update base. Please try again"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const deleteBase = async (id: number) => {
    setIsLoading(true)

    if (!id) {
      message.error(t(t("Invalid ID")))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.delete(`${API_PREFIX_BASESYSTEM_PATH}/${id}`)
      return response
    } catch (error) {
      message.error(t(t("Unable to update base. Please try again")))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAllBases = async (searchTerms: { [key: string]: string } = {}) => {
    const params: FilterSearchParams = {
      populate: "*",
      sort: "createdAt:desc",
      publicationState: "preview",
      filters: {},
      pagination: {
        pageSize: "100",
      },
      locale: localStorage.getItem("language") || undefined,
    }

    const excludedKeys = ["current", "pageSize", "start", "limit", "total", "page"]

    Object.keys(searchTerms).forEach(key => {
      if (!excludedKeys.includes(key)) {
        const value = searchTerms[key]

        if (key === "publicationState") {
          const convertParamStatusPost = value === "draft" || value === "all" ? "preview" : "live"
          params.publicationState = convertParamStatusPost

          if (value === "draft") {
            params.filters.publishedAt = { $null: true }
          } else {
            delete params.filters.publishedAt
          }
        } else if (key === "locale") {
          params.locale = value
        } else if (!excludedKeys.includes(key)) {
          if (value) {
            params.filters[key] = { $containsi: value }
          }
        } else if (value) {
          params.pagination = {
            ...params.pagination,
            [key]: value,
          }
        }
      } else {
        const value = searchTerms[key]
        if (value) {
          params.pagination = {
            ...params.pagination,
            [key]: value,
          }
        }
      }
    })

    try {
      const response = await HttpClient.get(`${API_PREFIX_BASESYSTEM_PATH}`, { params })

      const modifiedResponse = response.data.map((page: any) => ({
        ...page,
        status: page.attributes.publishedAt ? "published" : "draft",
        publishedAt: page.attributes.publishedAt,
      }))

      return {
        data: modifiedResponse,
        meta: response.meta,
      }
    } catch (error) {
      message.error(t("Failed to fetch base. Please try again"))
    }
  }

  const checkSlug = async (slug: string) => {
    const params: AxiosRequestConfig["params"] = {
      filters: {
        slug: { $eq: slug },
      },
      publicationState: "preview",
    }

    return await HttpClient.get(`${API_PREFIX_BASESYSTEM_PATH}`, { params })
  }

  return {
    isLoading,
    addBase,
    fetchBaseById,
    updateBase,
    deleteBase,
    fetchAllBases,
    checkSlug,
  }
}

export default baseService
